import { useConfirm } from "../confirm/ConfirmProvider";
import { toast } from "../toast/toast";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { IoAdd, IoArrowDown, IoArrowUp } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import useSWR from "swr";
import ActionIconButton from "../components/ActionIconButton";
import LoadingOverlay from "../components/Loading";
import ReagentGroupPopup from "../components/ReagentGroupPopup";
import ViewToggle from "../components/ViewToggle";
import { useUser } from "../contexts/UserContext";
import { ENDPOINT } from "../endpoint/endpoint";
import {
  checkReagentGroupUsed,
  deleteReagentGroup,
  getReagentCategoryList,
} from "../fetch/ReagentGroupRequest";
import { reagentFetcher } from "../fetch/SearchRequest";
import { defaultDir, SortField, useSort } from "../function/useSort";
import { useViewMode } from "../function/useViewMode";
import styles from "../styles/modules/ListPage.module.css";
import {
  Lab,
  ReagentCategory,
  ReagentGroup,
  ReagentGroupInfo,
} from "../types";

// 試薬マスターの「変更日」は、マスター自体の更新日時と配下の試薬の
// 更新日時のうち最も新しいものを採用する。
// 試薬の使用・返却では reagent_groups.updated_at が更新されないため、
// マスター自体の値だけでは「最近使ったもの」が上に来ない。
function lastUpdatedAt(info: ReagentGroupInfo): number {
  const groupTime = new Date(info.group.updated_at).getTime();
  let latest = isNaN(groupTime) ? 0 : groupTime;

  for (const reagent of info.reagents ?? []) {
    const time = new Date(reagent.updated_at).getTime();
    if (!isNaN(time) && time > latest) {
      latest = time;
    }
  }

  return latest;
}

function MasterListPage() {
  const {
    user,
    userLabs,
    memberships,
    units,
    isLoading: isUserLoading,
  } = useUser();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const stateLabId = (location.state as { labId?: number } | null)?.labId;

  const [viewMode, setViewMode] = useViewMode();
  const [sort, setSort] = useSort();
  const [searchMode, setSearchMode] = useState<"keyword" | "id">("keyword");
  const [input, setInput] = useState<string>(""); // 入力中の値
  const [appliedKeyword, setAppliedKeyword] = useState<string>(""); // 検索ボタンで確定した値
  const [categories, setCategories] = useState<ReagentCategory[]>([]);
  const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
  const [targetGroup, setTargetGroup] = useState<ReagentGroup | null>(null);

  // 対象研究室：選択画面から渡された研究室、なければ先頭
  const selectedLab: Lab | null = useMemo(() => {
    if (userLabs.length === 0) return null;
    return userLabs.find((lab) => lab.id === stateLabId) ?? userLabs[0];
  }, [userLabs, stateLabId]);

  const isLabAdmin = useMemo(
    () =>
      !!selectedLab &&
      memberships.some(
        (m) => m.role_code === "lab_admin" && m.lab_id === selectedLab.id
      ),
    [memberships, selectedLab]
  );

  const {
    data: groupInfos,
    isLoading,
    mutate,
  } = useSWR(
    selectedLab ? ENDPOINT.search.reagentList(selectedLab.id, "") : null,
    reagentFetcher
  );

  useEffect(() => {
    getReagentCategoryList().then(setCategories);
  }, []);

  // 所属研究室が無い場合（大学管理者のみ等）は研究室選択画面へ戻す。
  // 直接URLで内部ページに入っても空画面を表示しないようにする。
  useEffect(() => {
    if (!isUserLoading && userLabs.length === 0) {
      navigate("/labs", { replace: true });
    }
  }, [isUserLoading, userLabs, navigate]);

  const filtered = useMemo(() => {
    const list: ReagentGroupInfo[] = Array.isArray(groupInfos)
      ? groupInfos
      : [];
    const kw = appliedKeyword.trim().toLowerCase();
    const result = kw
      ? list.filter(
          (g) =>
            g.group.name.toLowerCase().includes(kw) ||
            (g.group.cas_number ?? "").toLowerCase().includes(kw)
        )
      : list;

    const dirMul = sort.dir === "asc" ? 1 : -1;
    const byName = (a: ReagentGroupInfo, b: ReagentGroupInfo) =>
      a.group.name.localeCompare(b.group.name, "ja");

    return result.slice().sort((a, b) => {
      if (sort.field === "cas") {
        const ca = a.group.cas_number ?? "";
        const cb = b.group.cas_number ?? "";
        // CAS未登録は方向に関係なく常に末尾
        if (!ca && !cb) return byName(a, b);
        if (!ca) return 1;
        if (!cb) return -1;
        const c = ca.localeCompare(cb, "ja", { numeric: true });
        return c !== 0 ? c * dirMul : byName(a, b);
      }
      if (sort.field === "created") {
        const da = new Date(a.group.created_at).getTime();
        const db = new Date(b.group.created_at).getTime();
        return da !== db ? (da - db) * dirMul : byName(a, b);
      }
      if (sort.field === "updated") {
        const da = lastUpdatedAt(a);
        const db = lastUpdatedAt(b);
        return da !== db ? (da - db) * dirMul : byName(a, b);
      }
      // name
      const n = byName(a, b);
      return n !== 0 ? n * dirMul : a.group.id - b.group.id;
    });
  }, [groupInfos, appliedKeyword, sort]);

  // 並び替え基準を変更（基準ごとの既定方向を適用）
  function changeSortField(field: SortField) {
    setSort({ field, dir: defaultDir(field) });
  }

  function toggleSortDir() {
    setSort({ field: sort.field, dir: sort.dir === "asc" ? "desc" : "asc" });
  }

  // 検索モード切替時は入力・適用済みキーワードをリセット
  function changeMode(mode: "keyword" | "id") {
    setSearchMode(mode);
    setInput("");
    setAppliedKeyword("");
  }

  function handleSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = input.trim();

    if (searchMode === "id") {
      const id = Number(value);
      if (!value || isNaN(id) || id <= 0) {
        toast.error("正しい試薬IDを入力してください。");
        return;
      }
      // 現在の研究室の試薬に存在するか確認してから遷移
      const list: ReagentGroupInfo[] = Array.isArray(groupInfos)
        ? groupInfos
        : [];
      const exists = list.some((g) => g.reagents.some((r) => r.id === id));
      if (!exists) {
        toast.error("該当する試薬が見つかりませんでした。");
        return;
      }
      navigate(`/reagents/${id}`);
      return;
    }

    // キーワード検索：ボタンを押したときだけ反映
    setAppliedKeyword(value);
  }

  function openReagents(info: ReagentGroupInfo) {
    navigate(`/reagents/group/${info.group.id}`, {
      state: { labId: selectedLab?.id },
    });
  }

  function openPopup(group: ReagentGroup | null) {
    setTargetGroup(group);
    setIsShowPopup(true);
  }

  async function handleDelete(group_id: number) {
    if (
      !(await confirm("本当に削除しますか？", {
        danger: true,
        confirmText: "削除",
      }))
    )
      return;
    const used = await checkReagentGroupUsed({
      group_id,
      tenant_id: user?.tenant_id ?? 0,
    });
    if (used !== 0) {
      toast.error(
        `この試薬マスターは${used}件の試薬で使用されているため削除できません。先にこのマスターのすべての試薬（廃棄された試薬を含む）を削除してから、もう一度お試しください。`
      );
      return;
    }
    await deleteReagentGroup({ group_id });
    mutate();
  }

  return (
    <>
      <div className={styles["list-page"]}>
        <div className={styles["content"]}>
        <button
          className={styles["back"]}
          onClick={() => navigate("/labs")}
        >
          ← 研究室選択
        </button>

        <div className={styles["title-block"]}>
          <h2 className={styles["page-title"]}>試薬マスター一覧</h2>
          {selectedLab && (
            <p className={styles["subtitle"]}>{selectedLab.name}</p>
          )}
        </div>

        <form className={styles["search-form"]} onSubmit={handleSearch}>
          <div className={styles["mode-toggle"]}>
            <button
              type="button"
              className={searchMode === "keyword" ? styles["active"] : ""}
              onClick={() => changeMode("keyword")}
            >
              語句
            </button>
            <button
              type="button"
              className={searchMode === "id" ? styles["active"] : ""}
              onClick={() => changeMode("id")}
            >
              ID
            </button>
          </div>
          <input
            className={styles["search"]}
            type="text"
            placeholder={
              searchMode === "id"
                ? "試薬IDを入力"
                : "試薬マスター名・CAS番号"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button type="submit" className={styles["search-btn"]}>
            検索
          </button>
        </form>

        {/* 並び替え（基準 + 昇降）と表示切替 */}
        <div className={styles["toolbar"]}>
          <div className={styles["sort-control"]}>
            <span className={styles["sort-label"]}>並び替え</span>
            <select
              className={styles["sort-select"]}
              value={sort.field}
              onChange={(e) => changeSortField(e.target.value as SortField)}
            >
              <option value="name">名前</option>
              <option value="cas">CAS番号</option>
              <option value="created">登録日</option>
              <option value="updated">変更日</option>
            </select>
            <button
              type="button"
              className={styles["sort-dir"]}
              onClick={toggleSortDir}
              title={sort.dir === "asc" ? "昇順" : "降順"}
              aria-label={sort.dir === "asc" ? "昇順" : "降順"}
            >
              {sort.dir === "asc" ? <IoArrowUp /> : <IoArrowDown />}
            </button>
          </div>
          <div className={styles["toolbar-actions"]}>
            {isLabAdmin && (
              <button
                type="button"
                className={styles["toolbar-add"]}
                onClick={() => openPopup(null)}
                title="マスター登録"
                aria-label="マスター登録"
              >
                <IoAdd />
              </button>
            )}
            <ViewToggle mode={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {filtered.length === 0 && !isLoading && (
          <p className={styles["empty"]}>試薬マスターがありません。</p>
        )}

        {viewMode === "card" ? (
          <div className={styles["card-grid"]}>
            {filtered.map((info) => (
              <div
                key={info.group.id}
                className={styles["card"]}
                onClick={() => openReagents(info)}
              >
                <div className={styles["card-title"]}>{info.group.name}</div>
                <dl className={styles["card-body"]}>
                  <div>
                    <dt>CAS</dt>
                    <dd>{info.group.cas_number || "-"}</dd>
                  </div>
                  <div>
                    <dt>トータル残量</dt>
                    <dd>
                      {info.totalQuantity}
                      {info.group.total_unit}
                    </dd>
                  </div>
                  <div>
                    <dt>サイズ別在庫</dt>
                    <dd>
                      {info.displaySizes.length === 0
                        ? "-"
                        : info.displaySizes.map((s) => (
                            <span key={s.id}>
                              {s.capacity_base}
                              {s.unit}（{s.stock}）
                            </span>
                          ))}
                    </dd>
                  </div>
                </dl>
                {isLabAdmin && (
                  <div
                    className={styles["card-actions"]}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionIconButton
                      variant="edit"
                      onClick={() => openPopup(info.group)}
                    />
                    <ActionIconButton
                      variant="delete"
                      onClick={() => handleDelete(info.group.id)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles["table-wrap"]}>
            <table className={styles["table"]}>
            <thead>
              <tr>
                <th>試薬マスター名</th>
                <th>CAS番号</th>
                <th>トータル残量</th>
                <th>サイズ別在庫</th>
                {isLabAdmin && <th>操作</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((info) => (
                <tr
                  key={info.group.id}
                  onClick={() => openReagents(info)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{info.group.name}</td>
                  <td>{info.group.cas_number || "-"}</td>
                  <td>
                    {info.totalQuantity}
                    {info.group.total_unit}
                  </td>
                  <td>
                    {info.displaySizes.map((s) => (
                      <div key={s.id}>
                        {s.capacity_base}
                        {s.unit}（{s.stock}）
                      </div>
                    ))}
                  </td>
                  {isLabAdmin && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <ActionIconButton
                        variant="edit"
                        onClick={() => openPopup(info.group)}
                      />
                      <ActionIconButton
                        variant="delete"
                        onClick={() => handleDelete(info.group.id)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}
        </div>
      </div>

      {isShowPopup && (
        <ReagentGroupPopup
          targetReagentGroup={targetGroup}
          reagentCategories={categories}
          selectedLab={selectedLab}
          units={units}
          onClose={() => setIsShowPopup(false)}
          onLoading={() => {}}
          updateReagentGroupList={() => mutate()}
        />
      )}

      {isLoading && <LoadingOverlay />}
    </>
  );
}

export default MasterListPage;
